import "./PaymentHistoryModal.scss";

import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import type { OptionType } from "components/inputs";
import { Select, TextInput } from "components/inputs";
import debounce from "lodash/debounce";
import { useMemo, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import type { CPTCodeResult, CPTCodeSelectOption } from "services/codes-api";
import {
  buildCPTCodeSelectedOption,
  mapCPTCodeResultToSelectOption,
  useGetCPTCodesByInputQuery,
  useGetDRGCodesByInputQuery,
  useGetHCPCSCodesByInputQuery,
  useGetICDCodesByInputQuery,
} from "services/codes-api";
import { useToken } from "utils/context";
import { formatCPTCodeOptionLabel } from "utils/cptCodeOptionLabel";

import type { PaymentHistoryFormData } from "./PaymentHistoryModal";

export type CodeOption = OptionType & {
  code?: string;
  id?: number;
  description?: string;
  indicator?: string;
  icd_indicator?: string;
  is_internal?: boolean;
};

export type CodeType = {
  code: string;
  description: string;
  indicator?: string;
  isNew?: boolean;
};

type CodeSectionProps = {
  codes: CodeType[];
  type: "CPT" | "DRG" | "HCPCS" | "ICD" | "NDC";
  errors: FieldErrors<PaymentHistoryFormData>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, updates: Partial<CodeType>) => void;
  onCorrectAllChange?: (
    index: number,
    type: string,
    updates: Partial<CodeType>,
  ) => void;
  showCorrectAll?: boolean;
};

type CodeRowProps = {
  code: CodeType;
  type: "CPT" | "DRG" | "HCPCS" | "ICD" | "NDC";
  index: number;
  onRemove: (index: number) => void;
  onChange: (index: number, updates: Partial<CodeType>) => void;
  onCorrectAllChange?: (
    index: number,
    type: string,
    updates: Partial<CodeType>,
  ) => void;
  showCorrectAll?: boolean;
};

const MAX_SEARCH_RESULTS = 25;

// utility function to check if a code is invalid
const isInvalidCode = (code: CodeType, type: string): boolean => {
  const description = code.description || "";

  switch (type) {
    case "ICD":
      return (
        description === "Invalid ICD code" ||
        description === "Missing ICD Indicator"
      );
    case "HCPCS":
      return (
        description === "Invalid HCPCS code" ||
        description === "Missing HCPCS Indicator"
      );
    case "CPT":
      return (
        description === "Invalid CPT code" ||
        description === "Missing CPT Indicator"
      );
    case "DRG":
      return (
        description === "Invalid DRG code" ||
        description === "Missing DRG Indicator"
      );
    case "NDC":
      return (
        description === "Invalid NDC code" ||
        description === "Missing NDC Indicator"
      );
    default:
      return false;
  }
};

const CodeRow = ({
  code,
  type,
  index,
  onRemove,
  onChange,
  onCorrectAllChange = () => {},
  showCorrectAll = false,
}: Omit<CodeRowProps, "errors">) => {
  const { accessToken } = useToken();
  const [codeSearchTerm, setCodeSearchTerm] = useState<string | null>(null);
  const [descSearchTerm, setDescSearchTerm] = useState<string | null>(null);
  const [isCorrectAll, setIsCorrectAll] = useState(false);

  // icd indicator options
  const icdIndicatorOptions = [
    { label: "ICD-10", value: "0" },
    { label: "ICD-9", value: "9" },
  ];

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setCodeSearchTerm(value);
      }, 500),
    [],
  );

  const debouncedSetDescSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setDescSearchTerm(value);
      }, 500),
    [],
  );

  const { data: cptCodeResults, isFetching: isCPTCodeFetching } =
    useGetCPTCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
        description__icontains: undefined,
      },
      {
        skip: !accessToken || !codeSearchTerm || type !== "CPT",
      },
    );

  const { data: drgCodeResults, isFetching: isDRGCodeFetching } =
    useGetDRGCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
        description__icontains: undefined,
      },
      {
        skip: !accessToken || !codeSearchTerm || type !== "DRG",
      },
    );

  const { data: hcpcsCodeResults, isFetching: isHCPCSCodeFetching } =
    useGetHCPCSCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
        description__icontains: undefined,
      },
      {
        skip: !accessToken || !codeSearchTerm || type !== "HCPCS",
      },
    );

  const { data: icdCodeResults, isFetching: isICDCodeFetching } =
    useGetICDCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
        icd_indicator: code.indicator || "0",
      },
      {
        skip: !accessToken || !codeSearchTerm || type !== "ICD",
      },
    );

  // Description queries
  const { data: cptDescResults, isFetching: isCPTDescFetching } =
    useGetCPTCodesByInputQuery(
      {
        code__istartswith: undefined,
        limit: MAX_SEARCH_RESULTS,
        description__icontains: descSearchTerm ?? undefined,
      },
      {
        skip: !accessToken || !descSearchTerm || type !== "CPT",
      },
    );

  const { data: drgDescResults, isFetching: isDRGDescFetching } =
    useGetDRGCodesByInputQuery(
      {
        code__istartswith: undefined,
        limit: MAX_SEARCH_RESULTS,
        description__icontains: descSearchTerm ?? undefined,
      },
      {
        skip: !accessToken || !descSearchTerm || type !== "DRG",
      },
    );

  const { data: hcpcsDescResults, isFetching: isHCPCSDescFetching } =
    useGetHCPCSCodesByInputQuery(
      {
        code__istartswith: undefined,
        limit: MAX_SEARCH_RESULTS,
        description__icontains: descSearchTerm ?? undefined,
      },
      {
        skip: !accessToken || !descSearchTerm || type !== "HCPCS",
      },
    );

  const { data: icdDescResults, isFetching: isICDDescFetching } =
    useGetICDCodesByInputQuery(
      {
        code__istartswith: undefined,
        limit: MAX_SEARCH_RESULTS,
        icd_indicator: code.indicator || "0",
      },
      {
        skip: !accessToken || !descSearchTerm || type !== "ICD",
      },
    );

  // Select the appropriate results based on type
  const codeResults = (() => {
    switch (type) {
      case "CPT":
        return cptCodeResults;
      case "DRG":
        return drgCodeResults;
      case "HCPCS":
        return hcpcsCodeResults;
      case "ICD":
        return icdCodeResults;
      default:
        return null;
    }
  })();

  const descResults = (() => {
    switch (type) {
      case "CPT":
        return cptDescResults;
      case "DRG":
        return drgDescResults;
      case "HCPCS":
        return hcpcsDescResults;
      case "ICD":
        return icdDescResults;
      default:
        return null;
    }
  })();

  const isCodeFetching = (() => {
    switch (type) {
      case "CPT":
        return isCPTCodeFetching;
      case "DRG":
        return isDRGCodeFetching;
      case "HCPCS":
        return isHCPCSCodeFetching;
      case "ICD":
        return isICDCodeFetching;
      default:
        return false;
    }
  })();

  const isDescFetching = (() => {
    switch (type) {
      case "CPT":
        return isCPTDescFetching;
      case "DRG":
        return isDRGDescFetching;
      case "HCPCS":
        return isHCPCSDescFetching;
      case "ICD":
        return isICDDescFetching;
      default:
        return false;
    }
  })();

  const codeOptions = useMemo(() => {
    if (!codeResults?.results) return [];

    if (type === "CPT") {
      return (codeResults.results as CPTCodeResult[]).map(
        mapCPTCodeResultToSelectOption,
      );
    }

    return codeResults.results.map((result: CodeOption) => ({
      label: `${result.code} - ${result.description}`,
      value: result.code,
      description: result.description,
      indicator: result.icd_indicator || result.indicator,
    }));
  }, [codeResults, type]);

  const descOptions = useMemo(() => {
    if (!descResults?.results) return [];

    if (type === "CPT") {
      return (descResults.results as CPTCodeResult[]).map(
        mapCPTCodeResultToSelectOption,
      );
    }

    return descResults.results.map((result: CodeOption) => ({
      label: `${result.code} - ${result.description}`,
      value: result.code,
      description: result.description,
      indicator: result.icd_indicator || result.indicator,
    }));
  }, [descResults, type]);

  const currentCode =
    type === "CPT"
      ? buildCPTCodeSelectedOption(
          code.code,
          code.description,
          codeOptions as CPTCodeSelectOption[],
          code.code,
        )
      : code.code
        ? {
            label: `${code.code}`,
            value: code.code,
            description: code.description,
            indicator: code.indicator,
          }
        : null;

  const currentDesc =
    type === "CPT"
      ? buildCPTCodeSelectedOption(
          code.code,
          code.description,
          descOptions as CPTCodeSelectOption[],
          code.description,
        )
      : code.description
        ? {
            label: code.description,
            value: code.code,
            description: code.description,
            indicator: code.indicator,
          }
        : null;

  // check if this code is invalid
  const isCodeInvalid = isInvalidCode(code, type);

  // create synthetic errors for invalid codes to trigger Select error styling
  const syntheticErrors = isCodeInvalid
    ? {
        [`${type.toLowerCase()}_code_${index}`]: {
          type: "invalid",
          message: code.description || "Invalid code",
        },
        [`${type.toLowerCase()}_description_${index}`]: {
          type: "invalid",
          message: code.description || "Invalid code",
        },
        ...(type === "ICD" && {
          [`${type.toLowerCase()}_indicator_${index}`]: {
            type: "invalid",
            message: code.description || "Invalid code",
          },
        }),
      }
    : {};

  const handleCodeChange = (o: CodeOption | null) => {
    if (!o) {
      onChange(index, {
        code: "",
        description: "",
        indicator: type === "ICD" ? code.indicator || "0" : undefined,
      });
      setIsCorrectAll(false);
      return;
    }

    onChange(index, {
      code: (o.code ?? o.value)?.toString() || "",
      description: o.description || "",
      indicator:
        type === "ICD" ? o.indicator || code.indicator || "0" : undefined,
    });
    setIsCorrectAll(false);
  };

  if (type === "NDC") {
    // NDC codes use manual input instead of API lookup
    return (
      <>
        <TextInput
          name={`${type.toLowerCase()}_code_${index}`}
          errors={syntheticErrors}
          placeholder="NDC Code"
          value={code.code}
          classNames="code-input"
          onChange={(e) => {
            onChange(index, {
              code: e.target.value,
              description: code.description,
            });
            setIsCorrectAll(false);
          }}
        />
        <TextInput
          name={`${type.toLowerCase()}_description_${index}`}
          errors={syntheticErrors}
          placeholder="Drug Name"
          value={code.description}
          classNames="code-description"
          onChange={(e) => {
            onChange(index, {
              code: code.code,
              description: e.target.value,
            });
            setIsCorrectAll(false);
          }}
        />
        {showCorrectAll && (
          <input
            type="checkbox"
            className="correct-all-checkbox"
            disabled={code.isNew}
            checked={isCorrectAll}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsCorrectAll(checked);

              if (checked) {
                onCorrectAllChange(index, "ndc_codes", {
                  code: code.code,
                  description: code.description,
                });
              } else {
                onCorrectAllChange(index, "ndc_codes", null);
              }
            }}
          />
        )}
        <figure>
          <ButtonIcon
            onClick={() => onRemove(index)}
            kind="neutral"
            size="small"
            className="delete"
          >
            delete
          </ButtonIcon>
        </figure>
      </>
    );
  }

  return (
    <>
      {type === "ICD" && (
        <Select
          name={`${type.toLowerCase()}_indicator_${index}`}
          errors={syntheticErrors}
          placeholder="ICD Indicator"
          options={icdIndicatorOptions}
          value={
            code.indicator
              ? icdIndicatorOptions.find((opt) => opt.value === code.indicator)
              : icdIndicatorOptions[0]
          }
          classNames="icd-indicator"
          onChange={(o: OptionType) => {
            onChange(index, {
              code: "",
              description: "",
              indicator: o?.value?.toString() || "0",
            });
          }}
        />
      )}
      <Select
        name={`${type.toLowerCase()}_code_${index}`}
        errors={syntheticErrors}
        placeholder={`${type} code`}
        noOptionsMessage={({ inputValue }) =>
          !inputValue
            ? `Begin typing a ${type} code...`
            : isCodeFetching
              ? "Searching..."
              : "No matches found."
        }
        options={codeOptions}
        value={currentCode}
        classNames="code-input"
        formatOptionLabel={(option: CodeOption, meta) =>
          type === "CPT"
            ? formatCPTCodeOptionLabel(option as CPTCodeSelectOption, meta)
            : option.label
        }
        onInputChange={(value: string) => debouncedSetSearchTerm(value)}
        onChange={handleCodeChange}
      />
      <Select
        name={`${type.toLowerCase()}_description_${index}`}
        errors={syntheticErrors}
        placeholder="Search description"
        noOptionsMessage={({ inputValue }) =>
          !inputValue
            ? "Begin typing a description..."
            : isDescFetching
              ? "Searching..."
              : "No matches found."
        }
        options={descOptions}
        value={currentDesc}
        tooltip={code.description || undefined}
        classNames="code-description"
        formatOptionLabel={(option: CodeOption, meta) =>
          type === "CPT"
            ? formatCPTCodeOptionLabel(option as CPTCodeSelectOption, meta)
            : option.label
        }
        onInputChange={(value: string) => debouncedSetDescSearchTerm(value)}
        onChange={handleCodeChange}
      />
      {showCorrectAll && (
        <input
          type="checkbox"
          className="correct-all-checkbox"
          disabled={code.isNew}
          checked={isCorrectAll}
          onChange={(e) => {
            const checked = e.target.checked;
            setIsCorrectAll(checked);
            let code_type = "diagnosis_codes";
            // Type assertion since NDC types return early and never reach this code
            const nonNdcType = type as Exclude<typeof type, "NDC">;
            switch (nonNdcType) {
              case "CPT":
                code_type = "cpt_codes";
                break;
              case "DRG":
                code_type = "drg_codes";
                break;
              case "HCPCS":
                code_type = "hcpcs_codes";
                break;
              case "ICD":
                code_type = "diagnosis_codes";
                break;
            }

            if (checked) {
              onCorrectAllChange(index, code_type, {
                code: code.code,
                description: code.description,
                indicator: code.indicator || "0",
              });
            } else {
              onCorrectAllChange(index, code_type, null);
            }
          }}
        />
      )}
      <figure>
        <ButtonIcon
          onClick={() => onRemove(index)}
          kind="neutral"
          size="small"
          className="delete"
        >
          delete
        </ButtonIcon>
      </figure>
    </>
  );
};

const CodeSection = ({
  codes,
  type,
  onAdd,
  onRemove,
  onChange,
  onCorrectAllChange = () => {},
  showCorrectAll = false,
}: Omit<CodeSectionProps, "errors">) => {
  return (
    <div
      className={`code-section ${type === "ICD" ? "with-indicator" : ""} ${
        showCorrectAll ? "with-correct-all" : ""
      }`}
    >
      {type === "ICD" && <div className="header-primary">ICD Indicator</div>}
      <div className="header-primary">
        {type === "NDC" ? "NDC Code" : `${type} Code`}
      </div>
      <div className="header-primary">
        {type === "NDC" ? "Drug Name" : "Description"}
      </div>
      {showCorrectAll && <div className="header-primary">Change All</div>}
      <div className="header-primary" style={{ justifyContent: "unset" }}>
        <ButtonIcon onClick={onAdd} kind="neutral" size="small">
          add_circle
        </ButtonIcon>
      </div>

      {codes.map((code, index) => (
        <CodeRow
          key={index}
          code={code}
          type={type}
          index={index}
          onRemove={onRemove}
          onChange={onChange}
          onCorrectAllChange={onCorrectAllChange}
          showCorrectAll={showCorrectAll}
        />
      ))}
    </div>
  );
};

export default CodeSection;
